// Third party library imports
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
    ApplicationConfig,
    importProvidersFrom,
    inject,
    provideAppInitializer,
} from '@angular/core';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { provideAuth, initializeAuth, browserSessionPersistence } from '@angular/fire/auth';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { GoogleMapsModule } from '@angular/google-maps';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatStepperModule } from '@angular/material/stepper';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyPresetModule } from '@ngx-formly/core/preset';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { provideLottieOptions } from 'ngx-lottie';

import { providePrimeNG } from 'primeng/config';
import Material from '@primeng/themes/material';

// Zopup library imports
import { provideZopCore } from '@zopup/zop-core';
import { provideZopApis } from '@zopup/zop-apis';

import {
    invalidDateRangeValidator,
    invalidOdometerValueValidator,
    leafNodeValidator,
    toFromDateValidator,
    yearDifferenceValidator,
    globalErrorInterceptor,
} from '@zopup/zop-shared';
import { provideZopUxCore, zopLoadingInterceptor } from '@zopup/zop-ux-core';
import { provideZopUxLayoutCore } from '@zopup/zop-ux-layout';

// Internal project imports
import { environment } from '../environment/environment';
import { appRoutes } from './app.routes';
import {
    maxLengthValidationMessage,
    maxValidationMessage,
    minLengthValidationMessage,
    minValidationMessage,
    types,
} from './formly.config';
import { mockApiServices } from './mock-api/mock-service.const';
import { provideIcons } from './providers/icons/icons.provider';
import { TranslocoHttpLoader } from './providers/transloco/transloco.http-loader';

const fbApp = () => initializeApp(environment.firebase);
const authApp = () => initializeAuth(fbApp(), {
    persistence: browserSessionPersistence,
});
const fbDb = () => getFirestore(fbApp());
const firebaseProviders = [
    provideFirebaseApp(fbApp),
    provideAuth(authApp),
    provideFirestore(fbDb),
];

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideAnimationsAsync(),
        // Consolidated HTTP client with all interceptors
        // provideHttpClient(withInterceptors([
        //     zopLoadingInterceptor,          // Loading indicator interceptor
        // ])),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Angular fire base provider
        importProvidersFrom(
            GoogleMapsModule,
            FormlyModule.forRoot({
                types: types,
                validators: [
                    {
                        name: 'toFromDateValidator',
                        validation: toFromDateValidator,
                    },
                    {
                        name: 'yearDifferenceValidator',
                        validation: yearDifferenceValidator,
                    },
                    { name: 'isLeafNode', validation: leafNodeValidator },
                    {
                        name: 'invalidOdometerValue',
                        validation: invalidOdometerValueValidator,
                    },
                    {
                        name: 'invalidDateRange',
                        validation: invalidDateRangeValidator,
                    },
                ],
                validationMessages: [
                    { name: 'required', message: 'This field is required' },
                    { name: 'minLength', message: minLengthValidationMessage },
                    { name: 'maxLength', message: maxLengthValidationMessage },
                    { name: 'min', message: minValidationMessage },
                    { name: 'max', message: maxValidationMessage },
                    {
                        name: 'toFromDateValidator',
                        message: 'To Date must be later than From Date',
                    },
                    {
                        name: 'yearDifferenceValidator',
                        message: 'Invalid Odometer Value',
                    },
                    {
                        name: 'invalidOdometerValue',
                        message: 'Invalid Odometer Value',
                    },
                    {
                        name: 'invalidDateRange',
                        message: 'Start Date must be before End Date',
                    },
                ],
            }),
            FormsModule,
            ReactiveFormsModule,
            FormlyMaterialModule,
            MatStepperModule,
            FormlyPresetModule
        ),

        ...firebaseProviders,

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'kn',
                        label: 'Kannada',
                    },
                    {
                        id: 'de',
                        label: 'German',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            // Preload the default language before the app starts to prevent empty/jumping content
            const initializerFn = (() => {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                // return () => firstValueFrom(translocoService.load(defaultLang));
                return (): Promise<void> => {
                    return new Promise<void>((resolve, reject) => {
                        translocoService.load(defaultLang).subscribe({
                            next: () => resolve(), // Resolve once the language is loaded
                            error: (err) => {
                                console.error(
                                    'Error loading default language:',
                                    err
                                );
                                reject(err); // Reject the promise on error
                            },
                        });
                    });
                };
            })();
            return initializerFn();
        }),

        // Zop Providers
        provideZopApis(environment),
        provideIcons(),
        provideZopCore({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            httpInterceptors: [
                globalErrorInterceptor,         // Global HTTP error handling interceptor
                zopLoadingInterceptor,          // Loading indicator interceptor
            ],
        }),
        provideZopUxCore(),
        provideZopUxLayoutCore({
            layout: 'modern',
            scheme: 'light',
            screens: {
                sm: '600px',
                md: '960px',
                lg: '1280px',
                xl: '1440px',
            },
            theme: 'theme-default',
            themes: [
                {
                    id: 'theme-default',
                    name: 'Default',
                },
                {
                    id: 'theme-brand',
                    name: 'Brand',
                },
                {
                    id: 'theme-teal',
                    name: 'Teal',
                },
                {
                    id: 'theme-rose',
                    name: 'Rose',
                },
                {
                    id: 'theme-purple',
                    name: 'Purple',
                },
                {
                    id: 'theme-amber',
                    name: 'Amber',
                },
            ],
            navigationUrl: 'api/common/navigation',
            isProduction: false,
            hideFooter: false,
            hideHeader: false,
            copyWriteText: '<a href="https://prestine.in" target="_blank" class="hover:text-primary transition-colors">Prestine Technologies Pvt. Ltd.</a> © {{currentYear}} All Rights Reserved.',
            footerStyle: 'detailed',
            footerDescription:
                'Verified by INFLEWZ is a product of Prestine Technologies Pvt. Ltd., developed to enhance product authenticity and consumer trust through advanced verification solutions.',
        }),

        provideLottieOptions({
            player: () => import('lottie-web'),
        }),

        // Prime Ng
        providePrimeNG({
            theme: {
                preset: Material,
                options: {
                    cssLayer: {
                        name: 'primeng',
                        order: 'tailwind-base, primeng, tailwind-utilities',
                    },
                    darkModeSelector: false, // Class to trigger dark mode
                },
            },
        }),
    ],
};
